<template>
  <section :class="['image-post-gallery', `variant-${variant}`]">
    <div
      ref="track"
      class="image-post-track"
      role="group"
      tabindex="0"
      :aria-label="$t('a11y.imagePosition', { current: currentIndex + 1, total: normalizedImages.length })"
      @scroll.passive="queueIndexUpdate"
      @keydown.left.prevent="moveBy(-1)"
      @keydown.right.prevent="moveBy(1)"
    >
      <img
        v-for="(imageUrl, imageIndex) in normalizedImages"
        :key="`${contentId || 'image-post'}-${imageIndex}`"
        :src="imageUrl"
        :alt="`${title} ${imageIndex + 1}`"
        loading="lazy"
        :draggable="false"
        @click.stop="preview(imageIndex)"
        @contextmenu.prevent
      >
    </div>
    <span v-if="normalizedImages.length" class="image-post-position" aria-live="polite">
      <b>{{ $n(currentIndex + 1) }}</b><i>/</i>{{ $n(normalizedImages.length) }}
    </span>
  </section>
</template>

<script>
import { openImageViewer } from "../utils/imageViewer"

export default {
  name: "ImagePostGallery",
  props: {
    images: { type: Array, default: () => [] },
    title: { type: String, default: "" },
    contentId: { type: [Number, String], default: "" },
    variant: { type: String, default: "feed" }
  },
  data() {
    return { currentIndex: 0, indexFrame: 0 }
  },
  computed: {
    normalizedImages() {
      return this.images.filter(imageUrl => typeof imageUrl === "string" && imageUrl.trim())
    }
  },
  watch: {
    normalizedImages() {
      this.currentIndex = 0
      this.$nextTick(() => {
        if (this.$refs.track) this.$refs.track.scrollTo({ left: 0, behavior: "auto" })
      })
    }
  },
  beforeDestroy() {
    window.cancelAnimationFrame(this.indexFrame)
  },
  methods: {
    queueIndexUpdate() {
      window.cancelAnimationFrame(this.indexFrame)
      this.indexFrame = window.requestAnimationFrame(this.updateCurrentIndex)
    },
    updateCurrentIndex() {
      const track = this.$refs.track
      if (!track || !track.children.length) return
      const trackCenter = track.getBoundingClientRect().left + track.clientWidth / 2
      let nearestIndex = 0
      let nearestDistance = Number.POSITIVE_INFINITY
      Array.from(track.children).forEach((image, index) => {
        const bounds = image.getBoundingClientRect()
        const distance = Math.abs(bounds.left + bounds.width / 2 - trackCenter)
        if (distance < nearestDistance) {
          nearestDistance = distance
          nearestIndex = index
        }
      })
      this.currentIndex = nearestIndex
    },
    moveBy(offset) {
      const track = this.$refs.track
      if (!track || !track.children.length) return
      const nextIndex = Math.max(0, Math.min(this.normalizedImages.length - 1, this.currentIndex + offset))
      track.children[nextIndex].scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
      this.currentIndex = nextIndex
    },
    preview(imageIndex) { openImageViewer(this.normalizedImages, imageIndex, { title: this.title }) }
  }
}
</script>

<style scoped>
.image-post-gallery{position:absolute;z-index:1;inset:0;overflow:hidden;background:#050609}
.image-post-track{width:100%;height:100%;display:flex;align-items:center;overflow-x:auto;overflow-y:hidden;scroll-snap-type:x mandatory;overscroll-behavior-inline:contain;scrollbar-width:none;touch-action:manipulation}
.image-post-track:focus-visible{outline:2px solid rgba(35,217,208,.72);outline-offset:-3px}
.image-post-track::-webkit-scrollbar{display:none}
.image-post-track img{display:block;width:100%;height:100%;flex:0 0 100%;object-fit:contain;object-position:center center;scroll-snap-align:center;scroll-snap-stop:always;background:#050609;-webkit-user-drag:none;-webkit-touch-callout:none;user-select:none;cursor:zoom-in}
.image-post-position{position:absolute;z-index:3;inset-inline-start:50%;bottom:calc(132px + env(safe-area-inset-bottom));display:flex;align-items:center;gap:3px;min-width:42px;justify-content:center;padding:5px 9px;border:1px solid rgba(255,255,255,.14);border-radius:14px;background:rgba(7,8,10,.62);color:rgba(255,255,255,.72);font-size:10px;font-variant-numeric:tabular-nums;line-height:1;box-shadow:0 6px 20px rgba(0,0,0,.22);backdrop-filter:blur(8px);transform:translateX(-50%)}
.image-post-position b{color:#fff;font-size:11px}.image-post-position i{opacity:.48;font-style:normal}
.variant-dialog{position:relative;width:100%;max-height:68vh;min-height:min(68vh,560px)}
.variant-dialog .image-post-track,.variant-dialog .image-post-track img{max-height:68vh}
.variant-dialog .image-post-position{bottom:14px}
[dir="rtl"] .image-post-position{transform:translateX(50%)}
@media(prefers-reduced-motion:reduce){.image-post-track{scroll-behavior:auto}}
</style>
